import type { Middleware } from 'redux';
import { posthog } from 'posthog-js';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { ModalActionPayLoadType } from '../features/globalModals/globalModalsSlice';
import {
  UserSettings,
  addTagToUserThunk,
} from '../features/settings/settingsSlice';
import { pushModal } from '../features/globalModals/globalModalsSlice';
import config from '../../config';
import { RootState } from '../reducers';
import { GetSubscriptionResponseData } from '../services/subscriptionServices';
import { createOrUpdateSubscription } from '../features/plan/thunk';
import { trackMediaAction } from '../features/library/actions/mediaActions/mediaActionsSlice';
import type { MediaActionPayload } from '../features/library/actions/mediaActions/mediaActionsSlice';
import { signOut } from '../features/auth/authSlice';
import {
  setSubscriptionState,
  trackCancelPlanModal,
  trackCancelPlanButton,
  trackDownloadInvoiceButton,
} from '../features/plan/planSlice';

// Initialize PostHog (make sure you replace 'YOUR_POSTHOG_API_KEY' and 'YOUR_POSTHOG_API_HOST' with actual values)
const { posthogApiKey, posthogApiHost } = config;
posthog.init(posthogApiKey, {
  api_host: posthogApiHost,
  capture_pageview: false, // disable automatic pageview tracking
  capture_pageleave: false, // disable automatic pageleave tracking
});

// Action types for onboarding tracking
const TRACK_ONBOARDING_GOAL = 'onboarding/trackGoal';
const TRACK_ONBOARDING_IMPORT_OPTIONS = 'onboarding/trackImportOptions';
const TRACK_ASSISTANT_QUERY = 'assistant/trackQuery';
const TRACK_ASSISTANT_ACTION = 'assistant/trackAction';
// Action types for shared video tracking
const TRACK_SHARED_QUERY = 'sharedVideo/trackQuery';
const TRACK_SHARED_VIEW = 'sharedVideo/trackView';
// Action type for template optimization tracking
const TRACK_OPTIMIZE_TEMPLATE = 'template/trackOptimization';
// Action type for template generation tracking
const TRACK_GENERATE_TEMPLATE = 'template/trackGeneration';

// Action types for navigation error tracking
const TRACK_NAVIGATION_ERROR = 'navigation/trackError';

// Action type for dictation tracking
const TRACK_NEW_DICTATION = 'dictation/trackNewDictation';

// Action type for blog try AI tracking
const TRACK_BLOG_TRY_AI = 'blog/tryAI';

// Action type for scene detection tracking
const TRACK_SCENE_DETECTION_TOGGLE = 'sceneDetection/trackToggle';

// Action type for Ask AI chat tracking
const TRACK_ASK_AI_CHAT = 'chat/trackAskAI';

// Action types for AI video detection tracking
const TRACK_AI_VIDEO_DETECTION_CLICK = 'aiVideoDetection/trackClick';
const TRACK_AI_VIDEO_DETECTION_RESULT = 'aiVideoDetection/trackResult';
const TRACK_AI_VIDEO_DETECTION_UNAVAILABLE =
  'aiVideoDetection/trackUnavailable';

// Action types for audio extraction tracking
const TRACK_AUDIO_EXTRACTION_CLICK = 'audioExtraction/trackClick';
const TRACK_AUDIO_EXTRACTION_RESULT = 'audioExtraction/trackResult';

// Action types for passkey tracking
const TRACK_PASSKEY_LOGIN = 'passkey/trackLogin';
const TRACK_PASSKEY_LOGIN_FAILED = 'passkey/trackLoginFailed';
const TRACK_PASSKEY_REGISTERED = 'passkey/trackRegistered';
const TRACK_PASSKEY_DELETED = 'passkey/trackDeleted';
const TRACK_PASSKEY_RENAMED = 'passkey/trackRenamed';

interface OnboardingGoalPayload {
  goal: string;
}

interface OnboardingImportOptionsPayload {
  importOptions: string[];
}

interface AssistantQueryPayload {
  query: string;
}

interface AssistantActionPayload {
  actionType: string;
  actionName: string;
  query?: string;
}

interface ActionMenuPayload {
  actionType: string;
  fileId: string;
  status?: string;
}

interface TemplateSelectionPayload {
  templateId: string;
  templateTitle: string;
}

// Add shared video query payload type
interface SharedQueryPayload {
  query: string;
  videoTitle: string;
}

// Add shared video view payload type
interface SharedViewPayload {
  videoTitle: string;
}

// Add subscription action payload type
interface SubscriptionActionPayload {
  plan: string;
  term: 'annual' | 'monthly';
  totalAmount: number;
}

// Track when user clicks the upgrade button with price
interface UpgradeButtonClickPayload {
  plan: string;
  term: 'annual' | 'monthly';
  amount: number;
  discountedAmount?: number;
}

const TRACK_UPGRADE_BUTTON_CLICK = 'upgrade/trackButtonClick';

interface TagPayload {
  key: string;
  value: string;
}

// Add template optimization payload type
interface OptimizeTemplatePayload {
  templateName: string;
  prompt: string;
}

// Add template generation payload type
interface GenerateTemplatePayload {
  prompt: string;
}

// Add navigation error payload type
interface NavigationErrorPayload {
  attemptedUrl: string;
  pathname: string;
  searchParams: string;
  hash: string;
  errorCode: number;
  errorTitle: string;
  userAgent: string;
  referrer: string;
}

// Add dictation payload type
interface DictationPayload {
  transcript: string;
  mode?: string;
}

// Add scene detection payload type
export interface SceneDetectionTogglePayload {
  enabled: boolean;
  fileId: string;
  duration?: number;
}

// Add Ask AI chat payload type
export interface AskAIChatPayload {
  chatType: 'video' | 'multifile' | 'singlefile' | 'search';
  question: string;
  fileCount: number;
  isVideoMode?: boolean;
  ocrEnabled?: boolean;
  sessionId: string | null;
  isNewSession: boolean;
}

// Add AI video detection payload types
export interface AiVideoDetectionClickPayload {
  fileId: string;
}

export interface AiVideoDetectionResultPayload {
  fileId: string;
  isAiGenerated: boolean;
  confidence: number;
  confidenceLevel: 'high' | 'medium' | 'low';
}

export interface AiVideoDetectionUnavailablePayload {
  fileId: string;
}

// Add audio extraction payload types
export interface AudioExtractionClickPayload {
  fileId: string;
}

export interface AudioExtractionResultPayload {
  fileId: string;
  status: 'completed' | 'failed';
  errorType?: 'no_audio_stream' | 'conversion_failed' | 'file_not_found';
}

// Passkey login payload types
export interface PasskeyLoginPayload {
  method: 'passkey';
}

export interface PasskeyLoginFailedPayload {
  method: 'passkey';
  error: string;
}

export interface PasskeyRegisteredPayload {
  name: string;
}

export interface PasskeyDeletedPayload {
  passkeyId: string;
}

export interface PasskeyRenamedPayload {
  passkeyId: string;
  newName: string;
}

const posthogMiddleware: Middleware<{}, RootState> =
  ({ getState }) =>
  (next) =>
  (
    action: PayloadAction<
      | UserSettings
      | ModalActionPayLoadType
      | GetSubscriptionResponseData
      | MediaActionPayload
      | OnboardingGoalPayload
      | OnboardingImportOptionsPayload
      | ActionMenuPayload
      | TemplateSelectionPayload
      | SubscriptionActionPayload
      | UpgradeButtonClickPayload
      | AssistantQueryPayload
      | AssistantActionPayload
      | SharedQueryPayload
      | SharedViewPayload
      | TagPayload
      | OptimizeTemplatePayload
      | GenerateTemplatePayload
      | NavigationErrorPayload
      | DictationPayload
      | SceneDetectionTogglePayload
      | AskAIChatPayload
      | AiVideoDetectionClickPayload
      | AiVideoDetectionResultPayload
      | AiVideoDetectionUnavailablePayload
      | AudioExtractionClickPayload
      | AudioExtractionResultPayload
      | PasskeyLoginPayload
      | PasskeyLoginFailedPayload
      | PasskeyRegisteredPayload
      | PasskeyDeletedPayload
      | PasskeyRenamedPayload
    >,
  ) => {
    const result = next(action);
    const { plan: planState } = getState();
    const { selectedPlan, sourceFeatureKey } = planState.upgrade;
    const state = getState();

    if (action.type === TRACK_ONBOARDING_GOAL) {
      const { goal } = action.payload as OnboardingGoalPayload;
      posthog.capture('onboarding-goal', {
        goal,
      });
      // Set this as a user property only if it hasn't been set before
      posthog.people.set_once({
        user_goal: goal,
      });
    }

    if (action.type === TRACK_ONBOARDING_IMPORT_OPTIONS) {
      const { importOptions } =
        action.payload as OnboardingImportOptionsPayload;

      // Create an object with each import option as a boolean property
      const importProperties = importOptions.reduce(
        (acc, option) => ({
          ...acc,
          [`import_${option.toLowerCase()}`]: true,
        }),
        {},
      );

      posthog.capture('onboarding-import-options', {
        ...importProperties,
        total_import_options: importOptions.length,
      });

      // Set these as user properties only once during onboarding
      posthog.people.set_once({
        ...importProperties,
        total_import_options: importOptions.length,
      });
    }

    if (action.type === setSubscriptionState.type) {
      const { settings } = state;
      const { name, email } = settings.user;
      const { subscription, marketing, usage, packageProperties } = planState;
      const { price, term } = subscription;
      const { intent } = marketing;

      posthog.identify(email);
      posthog.people.set({
        email,
        name,
      });
      posthog.people.set_once({
        intent,
      });
      posthog.register({
        subscription: {
          package: selectedPlan,
          price,
          term,
        },
        AIUsage: usage?.askAICount,
        VideoAIUsage: usage?.videoAskAICount,
        AILimit: packageProperties?.askAICount,
        VideoAILimit: packageProperties?.videoAskAICount,
        TranscriptionUsage: usage?.transcriptionCount,
        TranscriptionLimit: packageProperties?.transcriptionCount,
      });
    }

    if (action.type === pushModal.type) {
      const { type, source } = (action as PayloadAction<ModalActionPayLoadType>)
        .payload;
      if (
        type === 'upsell-modal' ||
        type === 'stripe-pricing-table-upgrade-modal' ||
        type === 'free-trial-modal'
      ) {
        const isFromLimits = source === 'limits';
        // Check which upgrade variant should be shown for A/B testing
        const upgradeVariant =
          state.settings.user.tags.feature_upgrade_variant_v2 === 'true'
            ? 'v2'
            : 'v1';
        // Track trial modal A/B test variant
        const showTrialModal =
          state.settings.user.tags.feature_show_trial_modal === 'true';
        posthog.capture('upgrade-modal', {
          selectedPlan,
          sourceFeatureKey,
          modalType: type,
          triggeredByLimits: isFromLimits,
          upgradeVariant, // Track A/B test variant
          trialModalVariant: showTrialModal ? 'trial' : 'upgrade', // Track trial vs upgrade modal
          AIUsage: planState.usage?.askAICount,
          VideoAIUsage: planState.usage?.videoAskAICount,
          AILimit: planState.packageProperties?.askAICount,
          VideoAILimit: planState.packageProperties?.videoAskAICount,
        });
      }
    }
    if (action.type === createOrUpdateSubscription.fulfilled.type) {
      const { subscription } = (
        action as PayloadAction<GetSubscriptionResponseData>
      ).payload;
      const { package: subscriptionPackage, price, term } = subscription;

      // Check if this is a trial signup by looking at the original action args
      const originalAction = action as any;
      const trialLabel = originalAction?.meta?.arg?.trialLabel;
      const isTrial = !!trialLabel;

      // Track trial signups separately from actual payments
      const eventName = isTrial ? 'post-trial' : 'post-payment';

      posthog.capture(eventName, {
        subscription: {
          package: subscriptionPackage,
          price,
          term,
        },
        ...(isTrial && { trialLabel }), // Include trial label if it's a trial
        sourceFeatureKey,
        AIUsage: planState.usage?.askAICount,
        VideoAIUsage: planState.usage?.videoAskAICount,
      });
      posthog.register({
        subscription: {
          package: subscriptionPackage,
          price,
          term,
        },
      });
    }
    if (action.type === trackMediaAction.type) {
      const {
        actionKey,
        actionLabel,
        intentKey,
        intentLabel,
        teamId,
        folderId,
      } = (action as PayloadAction<MediaActionPayload>).payload;
      posthog.capture('media-action-click', {
        actionKey,
        actionLabel,
        intentKey,
        intentLabel,
        teamId,
        folderId,
      });
    }
    if (action.type === signOut.type) {
      posthog.reset();
    }

    if (action.type === trackCancelPlanModal.type) {
      posthog.capture('cancel-plan-modal', {
        selectedPlan,
        sourceFeatureKey,
      });
    }

    if (action.type === trackCancelPlanButton.type) {
      posthog.capture('cancel-plan-button', {
        selectedPlan,
        sourceFeatureKey,
      });
    }

    // Track when user clicks the upgrade button with price
    if (action.type === TRACK_UPGRADE_BUTTON_CLICK) {
      const { plan, term, amount, discountedAmount } =
        action.payload as UpgradeButtonClickPayload;
      posthog.capture('upgrade-approve', {
        plan,
        term,
        amount,
        discountedAmount,
        sourceFeatureKey,
      });
    }

    // Track template selection
    if (action.type === 'templates/trackSelection') {
      const { templateId, templateTitle } =
        action.payload as TemplateSelectionPayload;
      posthog.capture('template-selected', {
        templateId,
        templateTitle,
      });
    }

    // Track action menu interactions
    if (action.type === 'actionMenu/trackAction') {
      const { actionType, fileId, status } =
        action.payload as ActionMenuPayload;
      posthog.capture('action-menu-click', {
        actionType,
        fileId,
        status: status || 'completed',
      });
    }

    // Track assistant queries
    if (action.type === TRACK_ASSISTANT_QUERY) {
      const { query } = action.payload as AssistantQueryPayload;
      posthog.capture('assistant-query', {
        query,
        timestamp: new Date().toISOString(),
      });
    }

    // Track assistant action clicks
    if (action.type === TRACK_ASSISTANT_ACTION) {
      const { actionType, actionName, query } =
        action.payload as AssistantActionPayload;
      posthog.capture('assistant-action-click', {
        actionType,
        actionName,
        query,
        timestamp: new Date().toISOString(),
      });
    }

    // Track shared video queries
    if (action.type === TRACK_SHARED_QUERY) {
      const { query, videoTitle } = action.payload as SharedQueryPayload;
      posthog.capture('shared-query', {
        query,
        videoTitle,
        timestamp: new Date().toISOString(),
      });
    }

    // Track shared video views
    if (action.type === TRACK_SHARED_VIEW) {
      const { videoTitle } = action.payload as SharedViewPayload;
      posthog.capture('shared-view', {
        videoTitle,
        timestamp: new Date().toISOString(),
      });
    }

    // Track template optimization requests
    if (action.type === TRACK_OPTIMIZE_TEMPLATE) {
      const { templateName, prompt } =
        action.payload as OptimizeTemplatePayload;
      posthog.capture('optimize-template', {
        templateName,
        prompt,
        timestamp: new Date().toISOString(),
      });
    }

    // Track template generation requests
    if (action.type === TRACK_GENERATE_TEMPLATE) {
      const { prompt } = action.payload as GenerateTemplatePayload;
      posthog.capture('generate-template', {
        prompt,
        timestamp: new Date().toISOString(),
      });
    }

    if (action.type === addTagToUserThunk.fulfilled.type) {
      const payload = action.payload as TagPayload;
      // Only track feature flags in PostHog
      if (payload.key.startsWith('feature_')) {
        posthog.people.set_once({
          [payload.key]: payload.value === 'true',
        });
      }
    }

    if (action.type === trackDownloadInvoiceButton.type) {
      const { invoiceId } = action.payload as unknown as { invoiceId: string };
      const userEmail = state.settings?.user?.email;
      posthog.capture('download-invoice-pdf', {
        invoiceId,
        userEmail,
      });
    }

    // Track navigation errors
    if (action.type === TRACK_NAVIGATION_ERROR) {
      const {
        attemptedUrl,
        pathname,
        searchParams,
        hash,
        errorCode,
        errorTitle,
        userAgent,
        referrer,
      } = action.payload as NavigationErrorPayload;

      posthog.capture('navigation-error', {
        attempted_url: attemptedUrl,
        pathname,
        search_params: searchParams,
        hash,
        error_code: errorCode,
        error_title: errorTitle,
        user_agent: userAgent,
        referrer,
        timestamp: new Date().toISOString(),
      });
    }

    // Track successful dictation
    if (action.type === TRACK_NEW_DICTATION) {
      const { transcript, mode } = action.payload as DictationPayload;
      posthog.capture('new-dictation', {
        transcript,
        mode,
        timestamp: new Date().toISOString(),
      });
    }

    // Track blog try AI button click
    if (action.type === TRACK_BLOG_TRY_AI) {
      posthog.capture('blog-try-ai', {
        timestamp: new Date().toISOString(),
      });
    }

    // Track scene detection toggle
    if (action.type === TRACK_SCENE_DETECTION_TOGGLE) {
      const { enabled, fileId, duration } =
        action.payload as SceneDetectionTogglePayload;
      posthog.capture('scene-detection-toggle', {
        enabled,
        fileId,
        duration,
        timestamp: new Date().toISOString(),
      });
    }

    // Track Ask AI chat queries
    if (action.type === TRACK_ASK_AI_CHAT) {
      const {
        chatType,
        question,
        fileCount,
        isVideoMode,
        ocrEnabled,
        sessionId,
        isNewSession,
      } = action.payload as AskAIChatPayload;

      posthog.capture('ask-ai-chat', {
        chatType,
        question,
        questionLength: question.length,
        fileCount,
        isVideoMode: isVideoMode || false,
        ocrEnabled: ocrEnabled || false,
        hasSession: !!sessionId,
        isNewSession,
        timestamp: new Date().toISOString(),
      });
    }

    // Track AI video detection button click
    if (action.type === TRACK_AI_VIDEO_DETECTION_CLICK) {
      const { fileId } = action.payload as AiVideoDetectionClickPayload;
      posthog.capture('ai-video-detection-click', {
        fileId,
        timestamp: new Date().toISOString(),
      });
    }

    // Track AI video detection result
    if (action.type === TRACK_AI_VIDEO_DETECTION_RESULT) {
      const { fileId, isAiGenerated, confidence, confidenceLevel } =
        action.payload as AiVideoDetectionResultPayload;
      posthog.capture('ai-video-detection-result', {
        fileId,
        isAiGenerated,
        confidence,
        confidenceLevel,
        timestamp: new Date().toISOString(),
      });
    }

    // Track AI video detection service unavailable
    if (action.type === TRACK_AI_VIDEO_DETECTION_UNAVAILABLE) {
      const { fileId } = action.payload as AiVideoDetectionUnavailablePayload;
      posthog.capture('ai-video-detection-unavailable', {
        fileId,
        timestamp: new Date().toISOString(),
      });
    }

    // Track audio extraction button click
    if (action.type === TRACK_AUDIO_EXTRACTION_CLICK) {
      const { fileId } = action.payload as AudioExtractionClickPayload;
      posthog.capture('audio-extraction-click', {
        fileId,
        timestamp: new Date().toISOString(),
      });
    }

    // Track audio extraction result
    if (action.type === TRACK_AUDIO_EXTRACTION_RESULT) {
      const { fileId, status, errorType } =
        action.payload as AudioExtractionResultPayload;
      posthog.capture('audio-extraction-result', {
        fileId,
        status,
        errorType,
        timestamp: new Date().toISOString(),
      });
    }

    // Track successful passkey login
    if (action.type === TRACK_PASSKEY_LOGIN) {
      posthog.capture('passkey-login', {
        method: 'passkey',
        timestamp: new Date().toISOString(),
      });
    }

    // Track failed passkey login
    if (action.type === TRACK_PASSKEY_LOGIN_FAILED) {
      const { error } = action.payload as PasskeyLoginFailedPayload;
      posthog.capture('passkey-login-failed', {
        method: 'passkey',
        error,
        timestamp: new Date().toISOString(),
      });
    }

    // Track passkey registered
    if (action.type === TRACK_PASSKEY_REGISTERED) {
      const { name } = action.payload as PasskeyRegisteredPayload;
      posthog.capture('passkey-registered', {
        name,
        timestamp: new Date().toISOString(),
      });
    }

    // Track passkey deleted
    if (action.type === TRACK_PASSKEY_DELETED) {
      const { passkeyId } = action.payload as PasskeyDeletedPayload;
      posthog.capture('passkey-deleted', {
        passkeyId,
        timestamp: new Date().toISOString(),
      });
    }

    // Track passkey renamed
    if (action.type === TRACK_PASSKEY_RENAMED) {
      const { passkeyId, newName } = action.payload as PasskeyRenamedPayload;
      posthog.capture('passkey-renamed', {
        passkeyId,
        newName,
        timestamp: new Date().toISOString(),
      });
    }

    return result;
  };

export default posthogMiddleware;

// Action creators for external use

// Action creator for tracking template optimization
export const trackOptimizeTemplate = (payload: OptimizeTemplatePayload) => ({
  type: TRACK_OPTIMIZE_TEMPLATE,
  payload,
});

// Action creator for tracking template generation
export const trackGenerateTemplate = (payload: GenerateTemplatePayload) => ({
  type: TRACK_GENERATE_TEMPLATE,
  payload,
});

// Action creator for tracking navigation errors
export const trackNavigationError = (payload: NavigationErrorPayload) => ({
  type: TRACK_NAVIGATION_ERROR,
  payload,
});

// Action creator for tracking new dictation
export const trackNewDictation = (payload: DictationPayload) => ({
  type: TRACK_NEW_DICTATION,
  payload,
});

// Action creator for tracking scene detection toggle
export const trackSceneDetectionToggle = (
  payload: SceneDetectionTogglePayload,
) => ({
  type: TRACK_SCENE_DETECTION_TOGGLE,
  payload,
});

// Action creator for tracking AI video detection button click
export const trackAiVideoDetectionClick = (
  payload: AiVideoDetectionClickPayload,
) => ({
  type: TRACK_AI_VIDEO_DETECTION_CLICK,
  payload,
});

// Action creator for tracking AI video detection result
export const trackAiVideoDetectionResult = (
  payload: AiVideoDetectionResultPayload,
) => ({
  type: TRACK_AI_VIDEO_DETECTION_RESULT,
  payload,
});

// Action creator for tracking AI video detection service unavailable
export const trackAiVideoDetectionUnavailable = (
  payload: AiVideoDetectionUnavailablePayload,
) => ({
  type: TRACK_AI_VIDEO_DETECTION_UNAVAILABLE,
  payload,
});

// Action creator for tracking audio extraction button click
export const trackAudioExtractionClick = (
  payload: AudioExtractionClickPayload,
) => ({
  type: TRACK_AUDIO_EXTRACTION_CLICK,
  payload,
});

// Action creator for tracking audio extraction result
export const trackAudioExtractionResult = (
  payload: AudioExtractionResultPayload,
) => ({
  type: TRACK_AUDIO_EXTRACTION_RESULT,
  payload,
});

// Action creator for tracking successful passkey login
export const trackPasskeyLogin = () => ({
  type: TRACK_PASSKEY_LOGIN,
  payload: { method: 'passkey' as const },
});

// Action creator for tracking failed passkey login
export const trackPasskeyLoginFailed = (
  payload: PasskeyLoginFailedPayload,
) => ({
  type: TRACK_PASSKEY_LOGIN_FAILED,
  payload,
});

// Action creator for tracking passkey registration
export const trackPasskeyRegistered = (payload: PasskeyRegisteredPayload) => ({
  type: TRACK_PASSKEY_REGISTERED,
  payload,
});

// Action creator for tracking passkey deletion
export const trackPasskeyDeleted = (payload: PasskeyDeletedPayload) => ({
  type: TRACK_PASSKEY_DELETED,
  payload,
});

// Action creator for tracking passkey rename
export const trackPasskeyRenamed = (payload: PasskeyRenamedPayload) => ({
  type: TRACK_PASSKEY_RENAMED,
  payload,
});
